import React, { CSSProperties, ReactNode, useMemo, useRef } from 'react';
import styled from 'styled-components';
import HBDivider from '../HBDivider/HBDivider';
import HBFlex from '../HBFlex/HBFlex';
import HBBox from '../HBBox/HBBox';
import { withStyles } from '../../styles/layout/withStyles';
import { vars } from '../../theme';
import { ListNavigationContext } from '../common/ListNavigationContext';
import { useListKeyboardNavigation } from '../common/useListKeyboardNavigation';

export type HBListProps = {
    /** HBListItem elements to display. Only elements with `data-list-item` participate in keyboard navigation. */
    children?: ReactNode;
    /** CSS max-height constraint on the outer list; content scrolls inside when it overflows. Accepts any CSS max-height value (e.g. '400px', '50vh'). */
    maxHeight?: CSSProperties['maxHeight'] & string;
    /** Named slots for fixed header and footer sections that remain visible while the list content scrolls. */
    slots?: {
        header?: ReactNode;
        footer?: ReactNode;
    };
    /** Disables built-in keyboard navigation (roving tabindex). @default false */
    disableKeyboardNavigation?: boolean;
    /** Whether arrow keys wrap around at list boundaries. @default true */
    loop?: boolean;
    /** Accessible label for the listbox when keyboard navigation is enabled, or when an explicit `role` is provided with `disableKeyboardNavigation`. */
    'aria-label'?: string;
    /** ID of the element that labels the listbox. Forwarded when navigation is enabled or when an explicit `role` is provided with `disableKeyboardNavigation`. */
    'aria-labelledby'?: string;
    /** Marks the listbox as a multi-select container. Only meaningful when `role="listbox"` is provided. */
    'aria-multiselectable'?: boolean;
    /**
     * Explicit ARIA role for the scroll container. Use with `disableKeyboardNavigation` when the
     * host component (e.g. a combobox) owns keyboard navigation and needs `role="listbox"` without
     * HBList's roving-tabindex taking over focus management.
     */
    role?: string;
    /** Called when the actively focused item changes via keyboard navigation. */
    onActiveItemChange?: (element: HTMLElement, index: number) => void;
};

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const HBList = ({
    children,
    maxHeight,
    slots,
    disableKeyboardNavigation = false,
    loop = true,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-multiselectable': ariaMultiselectable,
    role,
    onActiveItemChange
}: HBListProps) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);

    const navigationEnabled = !disableKeyboardNavigation;

    const { listContentProps } = useListKeyboardNavigation({
        containerRef: contentRef,
        loop,
        onActiveItemChange,
        enabled: navigationEnabled
    });

    const getNavigableItems = () =>
        Array.from(
            contentRef.current?.querySelectorAll<HTMLElement>(
                '[data-list-item][tabindex]:not([data-disabled="true"])'
            ) ?? []
        );

    const focusLastInHeader = (e: React.KeyboardEvent) => {
        const headerEl = headerRef.current;
        const focusables = headerEl
            ? Array.from(
                  headerEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
              )
            : [];
        const last = focusables[focusables.length - 1];
        if (last) {
            e.preventDefault();
            e.stopPropagation();
            last.focus();
        }
    };

    // Header: Tab / ArrowDown → first navigable item.
    // Both intercept before Radix can preventDefault on the event.
    const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
        const isTabForward = e.key === 'Tab' && !e.shiftKey;
        const isArrowDown = e.key === 'ArrowDown';
        if (!isTabForward && !isArrowDown) {
            return;
        }

        const firstItem = getNavigableItems()[0];
        if (firstItem) {
            e.preventDefault();
            e.stopPropagation();
            firstItem.focus();
        }
    };

    // Content: Tab → footer; Shift+Tab → header; ArrowUp from first item → header;
    // ArrowDown from last item → footer. All intercept before Radix.
    const handleContentKeyDown = (e: React.KeyboardEvent) => {
        const isTab = e.key === 'Tab';
        const isArrow = e.key === 'ArrowUp' || e.key === 'ArrowDown';
        if (!isTab && !isArrow) {
            return;
        }

        if (isTab && !e.shiftKey && slots?.footer) {
            const focusable =
                footerRef.current?.querySelector<HTMLElement>(
                    FOCUSABLE_SELECTOR
                );
            if (focusable) {
                e.preventDefault();
                e.stopPropagation();
                focusable.focus();
            }
            return;
        }

        if ((isTab && e.shiftKey) || e.key === 'ArrowUp') {
            if (!slots?.header) {
                return;
            }
            const items = getNavigableItems();
            // ArrowUp: only intercept when focus is on the first item so that
            // Radix can handle ArrowUp between all other items as normal.
            if (e.key === 'ArrowUp' && !items[0]?.contains(e.target as Node)) {
                return;
            }
            focusLastInHeader(e);
            return;
        }

        if (e.key === 'ArrowDown' && slots?.footer) {
            const items = getNavigableItems();
            const lastItem = items[items.length - 1];
            // Only intercept when focus is on the last item.
            if (!lastItem?.contains(e.target as Node)) {
                return;
            }
            const focusable =
                footerRef.current?.querySelector<HTMLElement>(
                    FOCUSABLE_SELECTOR
                );
            if (focusable) {
                e.preventDefault();
                e.stopPropagation();
                focusable.focus();
            }
        }
    };

    // Footer: Shift+Tab / ArrowUp → last navigable item.
    const handleFooterKeyDown = (e: React.KeyboardEvent) => {
        const isShiftTab = e.key === 'Tab' && e.shiftKey;
        const isArrowUp = e.key === 'ArrowUp';
        if (!isShiftTab && !isArrowUp) {
            return;
        }

        const items = getNavigableItems();
        const lastItem = items[items.length - 1];
        if (lastItem) {
            e.preventDefault();
            e.stopPropagation();
            lastItem.focus();
        }
    };

    const navigationContextValue = useMemo(
        () => ({
            enabled: navigationEnabled,
            suppressItemTabStop: !navigationEnabled
        }),
        [navigationEnabled]
    );

    return (
        <ListNavigationContext.Provider value={navigationContextValue}>
            <HBFlex
                displayName="StyledList"
                direction="column"
                maxHeight={maxHeight}
                overflow="hidden"
                flex="1"
            >
                {slots?.header && (
                    <HBBox
                        displayName="StyledHeader"
                        flex="0 0 auto"
                        ref={headerRef}
                        {...(disableKeyboardNavigation && {
                            onKeyDown: handleHeaderKeyDown
                        })}
                    >
                        {slots?.header}
                    </HBBox>
                )}
                <StyledContent
                    flex="1"
                    overflowY="auto"
                    ref={contentRef}
                    {...(disableKeyboardNavigation &&
                        (slots?.footer || slots?.header) && {
                            onKeyDown: handleContentKeyDown
                        })}
                    {...(navigationEnabled && {
                        ...listContentProps,
                        'aria-label': ariaLabel,
                        'aria-labelledby': ariaLabelledBy
                    })}
                    {...(!navigationEnabled &&
                        role && {
                            role,
                            'aria-label': ariaLabel,
                            'aria-labelledby': ariaLabelledBy,
                            'aria-multiselectable':
                                ariaMultiselectable ?? undefined
                        })}
                >
                    {children}
                </StyledContent>
                {slots?.footer && (
                    <HBBox
                        displayName="StyledFooter"
                        flex="0 0 auto"
                        ref={footerRef}
                        {...(disableKeyboardNavigation && {
                            onKeyDown: handleFooterKeyDown
                        })}
                    >
                        <HBDivider
                            margin="4px 0"
                            color={vars.colors.stroke.secondary}
                        />
                        {slots?.footer}
                    </HBBox>
                )}
            </HBFlex>
        </ListNavigationContext.Provider>
    );
};

// -------------------------------- Styles -------------------------------- //

/** Vertical padding so option focus rings (outline + offset on HBListItem inner) are not clipped by overflow-y on first/last rows. */
const LIST_CONTENT_FOCUS_RING_CLEARANCE = '6px';

// HBBox no longer accepts className directly — withStyles() re-enables
// styled() extension for hbui's own internal use (see ../layout/withStyles.tsx).
const StyledContent = styled(withStyles(HBBox))`
    /* Flex column, not block: hosts render flex-sized children inside the scroll
       container and need a definite height to resolve against. HBDropdown is the
       load-bearing case — react-select's MenuList (flex: 1, max-height: none) grows
       to its full content height under a block parent, stops being the scroll owner,
       and react-select's wheel capture (still bound to MenuList) then cancels every
       wheel event. See HON-33880 and invariant 4 in AGENTS.md. */
    display: flex;
    flex-direction: column;

    padding-block-start: var(
        --hb-list-content-padding-block-start,
        ${LIST_CONTENT_FOCUS_RING_CLEARANCE}
    );
    padding-block-end: var(
        --hb-list-content-padding-block-end,
        ${LIST_CONTENT_FOCUS_RING_CLEARANCE}
    );

    &:focus {
        outline: none;
    }
`;
